import { useEffect, useState } from 'react';
import { Dialog, Transition, DialogBackdrop, DialogPanel, Switch } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import axios from '../../../axios';
import notify from '../../../Utils/notify';

const HolidayModal = ({ open, onClose, holiday, sucursales }) => {
    const [data, setData] = useState({
        sucursal_id: '',
        fecha: '',
        nombre: '',
        descripcion: '',
        recurrente: false,
        activo: true
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            if (holiday) {
                // Modo edición
                setData({
                    sucursal_id: holiday.sucursal_id || '',
                    fecha: holiday.fecha,
                    nombre: holiday.nombre,
                    descripcion: holiday.descripcion || '',
                    recurrente: holiday.recurrente,
                    activo: holiday.activo
                });
            } else {
                // Modo creación
                resetForm();
            }
        }
    }, [open, holiday]);

    const resetForm = () => {
        setData({
            sucursal_id: '',
            fecha: '',
            nombre: '',
            descripcion: '',
            recurrente: false,
            activo: true
        });
    };

    const handleClose = () => {
        resetForm();
        onClose(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!data.fecha || !data.nombre) {
            notify.warning('Completa los campos requeridos');
            return;
        }

        setLoading(true);
        const payload = {
            ...data,
            sucursal_id: data.sucursal_id === '' ? null : data.sucursal_id
        };

        const request = holiday
            ? axios.put(`api/holidays/${holiday.id}`, payload)
            : axios.post('api/holidays', payload);

        request
            .then(res => {
                notify.success(res.data.message || 'Operación exitosa');
                setLoading(false);
                setTimeout(() => {
                    resetForm();
                    onClose(true); // Refresh parent
                }, 1500);
            })
            .catch(e => {
                setLoading(false);
                console.log("Error:", e);
                const message = e.response?.data?.message || 'Error al guardar el festivo';
                notify.error(message);
            });
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog onClose={handleClose} className="fixed z-50 inset-0 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4">
                    <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    <DialogPanel className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-auto z-50 relative">
                        {/* Header */}
                        <div className="flex justify-between items-center border-b bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <CalendarDaysIcon className="h-6 w-6 text-white" />
                                <h5 className="text-base font-semibold text-white">
                                    {holiday ? 'Editar Día Festivo' : 'Nuevo Día Festivo'}
                                </h5>
                            </div>
                            <button onClick={handleClose} className="text-white hover:text-gray-200">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Body */}
                        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                            {/* Fecha */}
                            <div>
                                <label htmlFor="fecha" className="block text-sm font-medium text-gray-700 mb-1">
                                    Fecha <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="fecha"
                                    type="date"
                                    value={data.fecha}
                                    onChange={(e) => setData({ ...data, fecha: e.target.value })}
                                    required
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Nombre */}
                            <div>
                                <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">
                                    Nombre del Festivo <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="nombre"
                                    type="text"
                                    value={data.nombre}
                                    onChange={(e) => setData({ ...data, nombre: e.target.value })}
                                    placeholder="Ej: Navidad, Año Nuevo"
                                    maxLength={100}
                                    required
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Descripción */}
                            <div>
                                <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-1">
                                    Descripción (opcional)
                                </label>
                                <textarea
                                    id="descripcion"
                                    value={data.descripcion}
                                    onChange={(e) => setData({ ...data, descripcion: e.target.value })}
                                    placeholder="Información adicional sobre este festivo"
                                    rows={2}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Sucursal */}
                            <div>
                                <label htmlFor="sucursal_id" className="block text-sm font-medium text-gray-700 mb-1">
                                    Sucursal
                                </label>
                                <select
                                    id="sucursal_id"
                                    value={data.sucursal_id}
                                    onChange={(e) => setData({ ...data, sucursal_id: e.target.value })}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 browser-default"
                                >
                                    <option value="">Todas las sucursales (Global)</option>
                                    {sucursales.map((suc) => (
                                        <option key={suc.id} value={suc.id}>
                                            {suc.nombre_sucursal}
                                        </option>
                                    ))}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                    Deja en blanco para aplicar a todas las sucursales
                                </p>
                            </div>

                            {/* Switches */}
                            <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                                {/* Recurrente */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Festivo Recurrente</label>
                                        <p className="text-xs text-gray-500">Se repetirá cada año automáticamente</p>
                                    </div>
                                    <Switch
                                        checked={data.recurrente}
                                        onChange={(checked) => setData({ ...data, recurrente: checked })}
                                        className={`${
                                            data.recurrente ? 'bg-blue-600' : 'bg-gray-300'
                                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                                    >
                                        <span
                                            className={`${
                                                data.recurrente ? 'translate-x-6' : 'translate-x-1'
                                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                        />
                                    </Switch>
                                </div>

                                {/* Activo */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Estado Activo</label>
                                        <p className="text-xs text-gray-500">Solo los activos bloquean la agenda</p>
                                    </div>
                                    <Switch
                                        checked={data.activo}
                                        onChange={(checked) => setData({ ...data, activo: checked })}
                                        className={`${
                                            data.activo ? 'bg-green-600' : 'bg-gray-300'
                                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                                    >
                                        <span
                                            className={`${
                                                data.activo ? 'translate-x-6' : 'translate-x-1'
                                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                        />
                                    </Switch>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end gap-2 pt-4 border-t">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                                    onClick={handleClose}
                                    disabled={loading}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    disabled={loading}
                                >
                                    {loading ? 'Guardando...' : holiday ? 'Actualizar' : 'Crear Festivo'}
                                </button>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </Dialog>
        </Transition>
    );
};

export default HolidayModal;
